/*
 * FEVR Penalty Shootout - shared rules.
 * Pure functions, no I/O. Used by the server (authoritative), the tests,
 * and the client (for display data + offline practice mode).
 *
 * Coordinates: goal-normalised. x in [-1, 1] (left post -> right post),
 * y in [0, 1] (ground -> crossbar). Shots can land outside this box (wide / over).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.FevrRules = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SHOTS_PER_LEVEL = 3;
  var LIVES = 3;
  var POINTS = 100;
  var STREAK_STEP = 0.25; // each consecutive goal adds this to the multiplier
  var STREAK_MAX = 3;     // multiplier cap (x3 after 8 goals in a row)
  var MAX_ATTEMPTS = 120; // safety cap so a run can't go on forever

  /*
   * read    : chance the keeper dives to where the ball is actually going
   * stay    : chance the keeper just stands there (baby energy)
   * rx, ry  : half-width / half-height of the area the keeper covers once he dives
   * cornerCut: how much worse the keeper is at the top corners (0..1)
   * period  : ms for the timing meter to sweep there and back
   * window  : ms the cursor spends inside the green zone on each pass
   */
  var KEEPERS = [
    { id: 'baby',         name: 'BABY',           tag: 'STILL ON THE BOTTLE',      read: 0.04, stay: 0.45, rx: 0.16, ry: 0.24, cornerCut: 0.60, period: 1700, window: 210 },
    { id: 'gmac',         name: 'GMAC',           tag: 'THE WARM-UP ACT',          read: 0.15, stay: 0.15, rx: 0.22, ry: 0.30, cornerCut: 0.55, period: 1500, window: 190 },
    { id: 'sunday',       name: 'SUNDAY LEAGUE',  tag: 'HAD A BIG NIGHT',          read: 0.22, stay: 0.08, rx: 0.27, ry: 0.34, cornerCut: 0.50, period: 1350, window: 170 },
    { id: 'nonleague',    name: 'NON-LEAGUE',     tag: 'TUESDAY NIGHT SPECIALIST', read: 0.30, stay: 0.00, rx: 0.31, ry: 0.38, cornerCut: 0.46, period: 1250, window: 150 },
    { id: 'championship', name: 'CHAMPIONSHIP',   tag: 'PLAYS 46 GAMES A SEASON',  read: 0.38, stay: 0.00, rx: 0.35, ry: 0.42, cornerCut: 0.42, period: 1150, window: 135 },
    { id: 'premier',      name: 'PREMIER LEAGUE', tag: 'SKY SPORTS APPROVED',      read: 0.46, stay: 0.00, rx: 0.39, ry: 0.46, cornerCut: 0.38, period: 1050, window: 120 },
    { id: 'courtois',     name: 'COURTOIS',       tag: 'TALL. VERY TALL.',         read: 0.54, stay: 0.00, rx: 0.44, ry: 0.52, cornerCut: 0.34, period: 980,  window: 105 },
    { id: 'octopus',      name: 'THE OCTOPUS',    tag: 'TOO MANY ARMS',            read: 0.60, stay: 0.00, rx: 0.48, ry: 0.56, cornerCut: 0.30, period: 920,  window: 95 },
    { id: 'robo',         name: 'ROBO-KEEPER',    tag: 'NO FEELINGS. NO MERCY.',   read: 0.66, stay: 0.00, rx: 0.52, ry: 0.60, cornerCut: 0.27, period: 860,  window: 85 },
    { id: 'void',         name: 'THE VOID',       tag: 'THE FINAL BOSS',           read: 0.72, stay: 0.00, rx: 0.56, ry: 0.64, cornerCut: 0.24, period: 800,  window: 75 }
  ];

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* Points for a goal when `streak` goals were scored in a row just before it. 0 -> 100, 1 -> 125, 2 -> 150 ... capped at x3. */
  function multFor(streak) { return Math.min(STREAK_MAX, 1 + STREAK_STEP * Math.max(0, streak | 0)); }
  function pointsFor(streak) { return Math.round(POINTS * multFor(streak)); }

  function levelFor(attempt) {
    return Math.min(Math.floor(attempt / SHOTS_PER_LEVEL), KEEPERS.length - 1);
  }

  /*
   * Half-width of the green zone as a fraction of the meter (0..1).
   * The cursor crosses the whole meter in period/2 ms, so the time spent in a zone
   * of total width 2*half is half*period  ->  half = window / period.
   */
  function sweetHalf(level) {
    var k = KEEPERS[level];
    return k.window / k.period;
  }

  /* Meter cursor position 0..1 at t ms after the meter started (ping-pong). */
  function meterPos(tMs, period) {
    var ph = (tMs % period) / period;
    return ph < 0.5 ? ph * 2 : 2 - ph * 2;
  }

  function meterInfo(level) {
    var k = KEEPERS[level];
    return { period: k.period, sweet: sweetHalf(level) };
  }

  /*
   * Resolve one penalty.
   *   level : keeper index
   *   aim   : {x, y} where the player tapped
   *   m     : meter position 0..1 at the moment of the shot (0.5 = centre of green zone)
   *   r     : array of >= 6 uniform random floats supplied by the caller (server secret)
   */
  function resolveShot(level, aim, m, r) {
    var K = KEEPERS[level];
    var half = sweetHalf(level);
    var dev = m - 0.5;
    var err = clamp((Math.abs(dev) - half) / 0.25, 0, 1); // 0 inside the zone .. 1 once the cursor is 25% of the bar away
    var perfect = err === 0;
    var x = aim.x, y = aim.y, weak = 0;
    var side = r[0] * 2 - 1;

    if (dev > 0) { // too late: leans back, blazes it high and wide
      y += err * 0.85 * (0.6 + 0.4 * r[6 % r.length]);
      x += side * err * 0.7;
    } else if (dev < 0) { // too early: scuffed, slow, easy to read
      weak = err;
      x += side * err * 0.45;
      y -= err * 0.25;
    }
    if (y < 0) y = 0;

    var outcome = null;
    var ax = Math.abs(x);
    if (y > 1.05) outcome = 'over';
    else if (ax > 1.05) outcome = 'wide';
    else if (ax >= 0.97 && y <= 1.0) outcome = 'post';
    else if (y > 1.0) outcome = 'bar';

    // keeper decision (made from server randomness, never visible to the client before the shot)
    var kx, ky, dive;
    var sx = clamp(x, -1, 1), sy = clamp(y, 0, 1);
    if (r[5] < K.stay) {
      kx = 0; ky = 0.3; dive = 'S';
    } else if (r[1] < K.read) {
      kx = sx + (r[2] * 2 - 1) * (1 - K.read) * 0.7;
      ky = sy + (r[3] * 2 - 1) * (1 - K.read) * 0.5;
    } else {
      kx = (r[4] < 0.5 ? -1 : 1) * (0.2 + r[2] * 0.75);
      ky = 0.1 + r[3] * 0.75;
    }
    kx = clamp(kx, -1, 1);
    ky = clamp(ky, 0, 1);
    if (!dive) dive = kx < -0.15 ? 'L' : kx > 0.15 ? 'R' : 'C';

    if (!outcome) {
      var bonus = 1 + weak * 0.9;
      var corner = clamp((ax - 0.7) / 0.3, 0, 1) * clamp((y - 0.55) / 0.35, 0, 1);
      var cf = 1 - K.cornerCut * corner;
      var rx = K.rx * bonus * cf, ry = K.ry * bonus * cf;
      var d = Math.pow((x - kx) / rx, 2) + Math.pow((y - ky) / ry, 2);
      outcome = d <= 1 ? 'save' : 'goal';
    }

    return {
      result: outcome,            // goal | save | wide | over | post | bar
      goal: outcome === 'goal',
      x: round3(x), y: round3(y), // where the ball actually went
      kx: round3(kx), ky: round3(ky), dive: dive,
      m: round3(m), perfect: perfect, weak: round3(weak)
    };
  }

  function round3(v) { return Math.round(v * 1000) / 1000; }

  return {
    SHOTS_PER_LEVEL: SHOTS_PER_LEVEL,
    LIVES: LIVES,
    POINTS: POINTS,
    MAX_ATTEMPTS: MAX_ATTEMPTS,
    STREAK_STEP: STREAK_STEP,
    STREAK_MAX: STREAK_MAX,
    multFor: multFor,
    pointsFor: pointsFor,
    KEEPERS: KEEPERS,
    levelFor: levelFor,
    sweetHalf: sweetHalf,
    meterPos: meterPos,
    meterInfo: meterInfo,
    resolveShot: resolveShot,
    clamp: clamp
  };
});
